import { useState, type FormEvent } from "react";
import "./password.css";
import { api } from "./api";
import type { Me } from "./session";

export const MIN_PASSWORD = 8;
/** Same rules as the server (users.ts ownPasswordProblem), shown before submitting. */
export function passwordHint(pw: string, confirm: string): string | null {
  if (!pw) return null;
  if (pw.length < MIN_PASSWORD) return `Use at least ${MIN_PASSWORD} characters (you have ${pw.length}).`;
  if (!pw.trim() || new Set(pw).size < 4) return "Too simple: use a mix of letters, numbers or words.";
  if (confirm && confirm !== pw) return "The two passwords don't match yet.";
  return null;
}

/** New password twice (+ current password when `askCurrent`). Never logs or echoes passwords.
 *  The button is never greyed out: autofill (iOS Keychain, password managers) can fill the boxes without
 *  React seeing an input event, so on submit we read the boxes themselves and explain any problem in words. */
export function PasswordForm({ askCurrent, submitLabel, onDone }: { askCurrent: boolean; submitLabel: string; onDone: (user: Me) => void }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hint = passwordHint(pw, confirm);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault(); if (busy) return;
    const f = e.currentTarget;
    const val = (n: string) => (f.elements.namedItem(n) as HTMLInputElement | null)?.value ?? "";
    const current = val("currentPassword"), next = val("newPassword"), again = val("confirmPassword");
    setPw(next); setConfirm(again);
    const problem = askCurrent && !current ? "Type your current password." : !next ? "Type a new password." : passwordHint(next, again) ?? (!again ? "Type the new password a second time." : next !== again ? "The two new passwords don't match." : null);
    if (problem) { setError(problem); return; }
    setBusy(true); setError(null);
    try {
      const r = await api.changePassword({ ...(askCurrent ? { currentPassword: current } : {}), newPassword: next, confirmPassword: again });
      f.reset(); setPw(""); setConfirm(""); onDone(r.user);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  const type = show ? "text" : "password";
  const sync = (e: FormEvent<HTMLFormElement>) => { const t = e.target as HTMLInputElement; if (t.name === "newPassword") setPw(t.value); if (t.name === "confirmPassword") setConfirm(t.value); setError(null); };
  return (
    <form className="pw-form" onSubmit={submit} onInput={sync} onChange={sync} noValidate>
      {askCurrent && <label className="pw-field"><span>Current password</span>
        <input name="currentPassword" type={type} autoComplete="current-password" /></label>}
      <label className="pw-field"><span>New password</span>
        <input name="newPassword" type={type} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} aria-describedby="pw-rules" /></label>
      <label className="pw-field"><span>New password again</span>
        <input name="confirmPassword" type={type} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} /></label>
      <label className="pw-show"><input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show passwords</label>
      <p id="pw-rules" className={`pw-hint ${hint ? "warn" : ""}`} role="status">{hint ?? `At least ${MIN_PASSWORD} characters. A short phrase is easy to remember, like "blue truck 441".${askCurrent ? "" : " It can't be the password you just used."}`}</p>
      {error && <div className="error" role="alert">{error}</div>}
      <button type="submit" className="primary big pw-submit" disabled={busy}>{busy ? "Saving…" : submitLabel}</button>
    </form>
  );
}

/** Shown instead of the whole app while the person is on a starting or temporary password. */
export function CreatePassword({ me, onDone, onSignOut }: { me: Me; onDone: (u: Me) => void; onSignOut: () => void }) {
  return (
    <div className="login">
      <div className="card login-card pw-card">
        <div className="brand big"><span className="logo" /> Centerline Cloud</div>
        <h2>Create your password</h2>
        <p>Hi {me.displayName}. You signed in with a starting password. Make your own password now to keep going. Only you will know it.</p>
        <PasswordForm askCurrent={false} submitLabel="Save my password" onDone={onDone} />
        <button type="button" className="pw-signout" onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}
